<script setup>
    const auth = useAuth()
    const email = ref('')
    const emailSent = ref(false)
    const busy = ref(false)
    const sendError = ref('')
    const route = useRoute();

    const isIframe = ref(route.path.includes('iframe'));

    const submit = async () => {
        // One request at a time. The button gave no sign of a send in
        // progress, so people clicked again: in PostHog, 133 of 172 login
        // rage-click sessions (2 weeks to 2026-10-13) submitted 4+ times,
        // each one a separate magic-link email.
        if (busy.value) return;
        busy.value = true;
        sendError.value = '';
        try {
            // better-auth's client resolves with `{ error }` rather than
            // throwing, so a failed request used to fall straight through to
            // "Thanks! Please check your email" with nothing sent.
            const result = await auth.client.signIn.magicLink({ email: email.value, name: email.value, callbackURL: isIframe.value ? "/iframe/my-ohc" : "/my-ohc" });
            if (result?.error) throw result.error;
            emailSent.value = true;
        } catch (error) {
            console.error('Failed to send magic link:', error);
            sendError.value = "We couldn't send your link. Check the email address and try again.";
        } finally {
            busy.value = false;
        }
    }

    const reset = () => {
        email.value = '';
        emailSent.value = false;
    }
</script>

<template>
    <section class="login-page" v-if="!emailSent">
        <div class="page container hd-container">
            <div class="header">
                <h1>Log in to My OHC</h1>
                <p>Instantly access your saved itineraries and favorited sites across devices. <strong>Enter your email to get a secure 1-click login link—no password needed</strong>. (Note: This login is separate from your CAC login credentials.)</p>
            </div>            
            <!-- The submit button deliberately has NO click handler. It used to carry
                 `@click.prevent="submit"`, which called submit() directly and so skipped
                 the browser's own validation entirely — "notanemail" sailed through to
                 "Thanks! Please check your email" (Asana O2-36). Letting the button
                 submit the form normally means `required`, `type="email"` and the
                 pattern below all get enforced first.

                 The pattern is there because `type="email"` alone accepts "a@b": the
                 spec doesn't require a dotted domain. This asks for something before
                 the @, something after it, and a dot with at least two more characters. -->
            <form method="post" @submit.prevent="submit" :class="{ busy: busy }">
                <label for="email">Email</label>
                <input
                    id="email"
                    type="email"
                    name="email"
                    v-model="email"
                    required
                    pattern="[^@\s]+@[^@\s]+\.[^@\s]{2,}"
                    title="Enter an email address, for example name@example.com"
                    autocomplete="email"
                    inputmode="email" />
                <button type="submit" :disabled="busy" :aria-busy="busy ? 'true' : undefined">{{ busy ? 'Sending…' : 'Send Me the Link' }}</button>
                <!-- Always rendered, so screen readers announce the message when
                     it appears rather than missing a region added after the fact. -->
                <p class="form-error" aria-live="polite">{{ sendError }}</p>
            </form>
            <div class="sub">
                <p><NuxtLink to="/my-ohc">Take me to My OHC without logging in.</NuxtLink></p>
                <p>Any items that you favorite or itineraries you create on this device will not be accessible on other devices if you choose not to log in.</p>
            </div>
        </div>
    </section>
    <section class="login-page" v-else>
        <div class="page container hd-container">
            <div class="header">
                <div class="icon-circle">
                    <SvgsEmailSent />
                </div>
                <h1>Thanks! Please check your email</h1>
                <div class="confirm">
                    <p>We’ve sent a link to <strong>{{ email }}</strong> that automatically logs you in to access your saved OHC itineraries and favorites—no password needed.</p>
                    <p>If you didn’t receive the link, please check your spam folder or <button type="button" class="link-button" @click="reset">request a new link</button>.</p>
                </div>
            </div>
        </div>
    </section>
</template>

<style lang="scss" scoped>
    .login-page {
        padding: 20px 0 0 0;

        @media (min-width: $tablet) {
            width: 100vw;
            max-width: $hd;
            margin: auto;
        }

        h1 {
            color: $black;
            font-family: $avenir-next;
            font-size: 32px;
            font-style: normal;
            font-weight: 700;
            line-height: 32px;
            text-transform: uppercase;
            margin-bottom: 16px;
        }

        p {
            color: $black;
            font-size: 16px;
            font-style: normal;
            font-weight: 350;
            line-height: 22px;

            i {
                font-style: normal;
                color: $red;
                font-weight: 800;
            }

            a,
            .link-button {
                text-decoration-line: underline;
                text-decoration-style: solid;
                text-decoration-skip-ink: none;
                text-decoration-thickness: auto;
                text-underline-offset: auto;
                text-underline-position: from-font;

                &:hover {
                    color: $red;
                }
            }

            .link-button {
                cursor: pointer;
            }
        }

        .header {
            margin-bottom: 25px;
            display: flex;
            flex-direction: column;
        }

        .icon-circle {
            display: flex;
            justify-content: center;
            border-radius: 100%;
            border: 1px solid #9D9FA2;
            width: 55px;
            height: 55px;
            margin-bottom: 16px;
            
            svg {
                position: relative;
                margin-top: -3px;
            }
        }

        .confirm p {
            margin-bottom: 16px;
        }

        form {
            display: flex;
            flex-direction: column;
            margin-bottom: 30px;

            label {
                color: #000;
                font-size: 11px;
                font-style: normal;
                font-weight: 500;
                line-height: 15px;
                letter-spacing: 0.77px;
                text-transform: uppercase;
                margin-bottom: 6px;
            }

            input {
                border: 1px solid #9D9FA2;
                background: var(--White, #FFF);
                padding: 16px;
                margin-bottom: 13px;
                display: block;
            }

            button {
                display: block;
                display: flex;
                padding: 15px 10px;
                justify-content: center;
                align-items: center;
                gap: 10px;
                background: $red;
                color: white;
                text-align: center;
                font-family: $avenir-next;
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: normal;
                text-transform: uppercase;

                // Sending: visibly not ready for another click.
                &:disabled {
                    opacity: 0.6;
                    cursor: progress;
                }
            }

            .form-error {
                color: $red;
                font-weight: 500;
                margin-top: 12px;

                &:empty {
                    margin-top: 0;
                }
            }
        }

        .sub {
            p {
                color: $black;
                font-size: 14px;
                font-style: normal;
                font-weight: 350;
                line-height: 17px;
                margin-bottom: 16px;

                a {
                    text-decoration-line: underline;
                    text-decoration-style: solid;
                    text-decoration-skip-ink: none;
                    text-decoration-thickness: auto;
                    text-underline-offset: auto;
                    text-underline-position: from-font;

                    &:hover {
                        color: $red;
                    }
                }
            }
        }
    }
</style>